import isNil from 'lodash/isNil';

import { metricsAxios } from 'api/apiBase/axios';
import { IBaseResponse } from 'api/apiBase/IBaseResponse';
import { mapError } from 'api/apiBase/mapError';
import { mapResponse } from 'api/apiBase/mapResponse';

import type {
  GetTransferHistoryRequest,
  GetTransferHistoryResponse,
  TaskBalancer,
  TaskBalancerAccount,
  TaskBalancerGroup,
  TaskBalancerQueryParams,
  TaskBalancerToken,
} from '../model/types';

// Task operations
export const getBalancerTask = async ({
  projectId,
  groupIds,
  limit,
  offset,
}: TaskBalancerQueryParams) => {
  try {
    const urlParams = new URLSearchParams();

    if (!isNil(projectId)) {
      urlParams.append('project_id', projectId.toString());
    }
    urlParams.append('limit', limit.toString());
    urlParams.append('offset', offset.toString());

    if (groupIds) {
      for (const groupId of groupIds) {
        urlParams.append('group_ids', String(groupId));
      }
    }

    const response = await metricsAxios.get<
      IBaseResponse<{ tasks: TaskBalancer[]; total_count: number }>
    >(`/api/v1/wallet-balancer/task?${urlParams.toString()}`);

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<{ tasks: TaskBalancer[]; total_count: number }>({ error });
  }
};

export const createBalancerTask = async (body: TaskBalancer) => {
  try {
    const response = await metricsAxios.post<IBaseResponse<TaskBalancer>>(
      '/api/v1/wallet-balancer/task',
      { task: body },
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<TaskBalancer>({ error });
  }
};

export const updateBalancerTask = async (groupId: number, body: TaskBalancerGroup) => {
  try {
    const response = await metricsAxios.put<IBaseResponse<{ success: boolean }>>(
      `/api/v1/wallet-balancer/task/${groupId}`,
      body,
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<{ success: boolean }>({ error });
  }
};

/**
 * Update active status of a task group
 * @param groupId - The group ID
 * @param active - The active status
 * @returns The active status
 * @see https://api-metrics-dev.111pg.info/swagger/index.html#/wallet-balancer/put_api_v1_wallet_balancer_task_state__group_id_
 */
export const updateBalancerTaskStatus = async (groupId: number, active: boolean) => {
  try {
    const response = await metricsAxios.put<IBaseResponse<{ active: boolean }>>(
      `/api/v1/wallet-balancer/task/state/${groupId}`,
      { active },
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<{ success: boolean }>({ error });
  }
};

export const removeBalancerTask = async (groupId: number) => {
  try {
    const response = await metricsAxios.delete<IBaseResponse<{ success: boolean }>>(
      `/api/v1/wallet-balancer/task/${groupId}`,
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError({ error });
  }
};

// Account operations
export const createAccount = async (body: TaskBalancerAccount) => {
  try {
    const response = await metricsAxios.post<IBaseResponse<TaskBalancerAccount>>(
      '/api/v1/wallet-balancer/account',
      body,
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<TaskBalancerAccount>({ error });
  }
};

export const updateAccount = async (body: TaskBalancerAccount) => {
  try {
    const response = await metricsAxios.patch<IBaseResponse<{ account: TaskBalancerAccount }>>(
      '/api/v1/wallet-balancer/account',
      { account: body },
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<{ account: TaskBalancerAccount }>({ error });
  }
};

export const removeAccount = async ({
  accountId,
  groupId,
}: {
  accountId: number;
  groupId: number;
}) => {
  try {
    const urlParams = new URLSearchParams();

    urlParams.append('group_id', groupId.toString());

    const response = await metricsAxios.delete<IBaseResponse<{ success: boolean }>>(
      `/api/v1/wallet-balancer/account/${accountId}?${urlParams.toString()}`,
    );

    return mapResponse({ data: response.data });
  } catch (error: unknown) {
    return mapError({ error });
  }
};

// Token operations
export const createToken = async (body: TaskBalancerToken) => {
  try {
    const response = await metricsAxios.post<IBaseResponse<{ token: TaskBalancerToken }>>(
      '/api/v1/wallet-balancer/token',
      body,
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<{ token: TaskBalancerToken }>({ error });
  }
};

export const updateToken = async (body: TaskBalancerToken) => {
  try {
    const response = await metricsAxios.patch<IBaseResponse<{ token: TaskBalancerToken }>>(
      '/api/v1/wallet-balancer/token',
      { token: body },
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<{ token: TaskBalancerToken }>({ error });
  }
};

export const removeToken = async ({ tokenId, groupId }: { tokenId: number; groupId: number }) => {
  try {
    const urlParams = new URLSearchParams();

    urlParams.append('group_id', groupId.toString());

    const response = await metricsAxios.delete<IBaseResponse<{ success: boolean }>>(
      `/api/v1/wallet-balancer/token/${tokenId}?${urlParams.toString()}`,
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError({ error });
  }
};

// Transfer History operations
/**
 * Get transfer history for a wallet balancer group
 * @param params - Query parameters including group_id, limit, and offset
 * @returns Transfer history response with transfers and total count
 * @see https://api-metrics-dev.111pg.info/swagger/index.html#/wallet-balancer/get_api_v1_wallet_balancer_transfer_history__group_id_
 */
export const getTransferHistory = async ({
  group_id,
  limit,
  offset,
}: GetTransferHistoryRequest) => {
  try {
    const urlParams = new URLSearchParams();

    urlParams.append('limit', limit.toString());
    urlParams.append('offset', offset.toString());

    const response = await metricsAxios.get<IBaseResponse<GetTransferHistoryResponse>>(
      `/api/v1/wallet-balancer/transfer/history/${group_id}?${urlParams.toString()}`,
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<GetTransferHistoryResponse>({ error });
  }
};
